import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { compressImage } from '../lib/image';
import { Upload, Trash2, Loader2, Eye, EyeOff, Save } from 'lucide-react';
import { toast } from 'sonner';

const AdminGallery = () => {
  const { refresh } = useSiteConfig();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const r = await api.get('/admin/gallery');
    setItems(r.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const data = await compressImage(f, { maxW: 1600, maxH: 1600, quality: 0.82 });
        await api.post('/admin/gallery', {
          image: data, category: 'general', caption: '', order: items.length, visible: true,
        });
      }
      await load();
      refresh();
      toast.success(`Uploaded ${files.length} image(s)`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    await api.delete(`/admin/gallery/${id}`);
    setItems((a) => a.filter((x) => x.id !== id));
    refresh();
  };

  const update = async (item, patch) => {
    const next = { ...item, ...patch };
    setItems((a) => a.map((x) => x.id === item.id ? next : x));
    await api.put(`/admin/gallery/${item.id}`, next);
    refresh();
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-[#5a1d3b]" style={{ fontWeight: 500 }}>Gallery</h1>
          <p className="text-gray-500 text-sm">Upload and manage gallery images.</p>
        </div>
        <label className="inline-flex items-center gap-2 bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full h-11 px-5 cursor-pointer text-sm font-medium">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Upload Images
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => onFiles(Array.from(e.target.files || []))}
          />
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="aspect-square bg-gray-50 relative">
              <img src={g.image} alt="" className="w-full h-full object-cover" />
              {!g.visible && (
                <div className="absolute top-2 left-2 bg-white/90 text-xs px-2 py-0.5 rounded">Hidden</div>
              )}
            </div>
            <div className="p-3 space-y-2">
              <Input
                value={g.caption || ''}
                onChange={(e) => setItems((a) => a.map((x) => x.id === g.id ? { ...x, caption: e.target.value } : x))}
                onBlur={() => update(g, { caption: g.caption })}
                placeholder="Caption (optional)"
                className="h-9 text-sm"
              />
              <div className="flex items-center justify-between">
                <button
                  onClick={() => update(g, { visible: !g.visible })}
                  className="text-xs text-gray-600 hover:text-[#c4477a] flex items-center gap-1"
                >
                  {g.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {g.visible ? 'Visible' : 'Hidden'}
                </button>
                <button onClick={() => remove(g.id)} className="text-red-600 p-1 hover:bg-red-50 rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && <p className="text-center text-gray-500 py-12">No images yet.</p>}
    </div>
  );
};

export default AdminGallery;
